
export default function ComparisonTable() {
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Lean Construction Comparison
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            See the real cost savings of contracting your layout and detailing work versus hiring a full-time employee
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-900 text-white">
                  <th className="px-6 py-5 text-left text-lg font-semibold">Expense Category</th>
                  <th className="px-6 py-5 text-left text-lg font-semibold">Full-Time Employee (FTE)</th>
                  <th className="px-6 py-5 text-left text-lg font-semibold bg-teal-600">Dinari Designs (Contract)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="px-6 py-5 font-semibold text-slate-900">Salary/Fee</td>
                  <td className="px-6 py-5 text-slate-700">
                    <div>$85k - $110k+ (Fixed)</div>
                    <div className="text-sm text-slate-500 mt-1">Paid regardless of workload</div>
                  </td>
                  <td className="px-6 py-5 text-teal-700 font-semibold">
                    <div>Project-Based (Variable)</div>
                    <div className="text-sm text-teal-600 mt-1">Pay only for active projects</div>
                  </td>
                </tr>
                <tr className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="px-6 py-5 font-semibold text-slate-900">BC Payroll Taxes</td>
                  <td className="px-6 py-5 text-slate-700">
                    <div>CPP, EI, EHT (Extra 10%+)</div>
                    <div className="text-sm text-slate-500 mt-1">$8,500 - $11,000+/year</div>
                  </td>
                  <td className="px-6 py-5 text-teal-700 font-semibold">
                    <div>$0</div>
                    <div className="text-sm text-teal-600 mt-1">No employer taxes</div>
                  </td>
                </tr>
                <tr className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="px-6 py-5 font-semibold text-slate-900">Benefits/Vacation</td>
                  <td className="px-6 py-5 text-slate-700">
                    <div>Health, Dental, 3-week PTO</div>
                    <div className="text-sm text-slate-500 mt-1">$5,000 - $12,000+/year</div>
                  </td>
                  <td className="px-6 py-5 text-teal-700 font-semibold">
                    <div>$0</div>
                    <div className="text-sm text-teal-600 mt-1">No benefits overhead</div>
                  </td>
                </tr>
                <tr className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="px-6 py-5 font-semibold text-slate-900">Software Licenses</td>
                  <td className="px-6 py-5 text-slate-700">
                    <div>$2,500+/yr (AutoCAD/Bluebeam)</div>
                    <div className="text-sm text-slate-500 mt-1">Plus workstation hardware</div>
                  </td>
                  <td className="px-6 py-5 text-teal-700 font-semibold">
                    <div>Included</div>
                    <div className="text-sm text-teal-600 mt-1">All tools provided</div>
                  </td>
                </tr>
                <tr className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="px-6 py-5 font-semibold text-slate-900">Training/Onboarding</td>
                  <td className="px-6 py-5 text-slate-700">
                    <div>2-4 weeks downtime</div>
                    <div className="text-sm text-slate-500 mt-1">Lost productivity cost</div>
                  </td>
                  <td className="px-6 py-5 text-teal-700 font-semibold">
                    <div>Instant Start</div>
                    <div className="text-sm text-teal-600 mt-1">20 years BC experience</div>
                  </td>
                </tr>
                <tr className="hover:bg-slate-50">
                  <td className="px-6 py-5 font-semibold text-slate-900">Market Risk</td>
                  <td className="px-6 py-5 text-slate-700">
                    <div>Cost remains if project stalls</div>
                    <div className="text-sm text-slate-500 mt-1">Fixed overhead burden</div>
                  </td>
                  <td className="px-6 py-5 text-teal-700 font-semibold">
                    <div>Zero cost during delays</div>
                    <div className="text-sm text-teal-600 mt-1">Complete flexibility</div>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="bg-slate-900 text-white">
                  <td className="px-6 py-5 font-bold text-lg">Total Annual Cost</td>
                  <td className="px-6 py-5 font-bold text-lg">$110,000 - $145,000+</td>
                  <td className="px-6 py-5 font-bold text-lg bg-teal-600">Project-Based Only</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="mt-12 bg-teal-50 border-l-4 border-teal-600 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex items-center justify-center w-12 h-12 bg-teal-600 rounded-lg flex-shrink-0">
              <i className="ri-lightbulb-line text-2xl text-white"></i>
            </div>
            <div className="ml-4">
              <h3 className="text-xl font-bold text-slate-900 mb-2">The Lean Construction Advantage</h3>
              <p className="text-slate-700 leading-relaxed">
                By converting fixed overhead into variable project costs, firms across Vancouver, Surrey, Burnaby, Coquitlam, and the Fraser Valley can stay competitive and agile. You get expert-level AutoCAD precision exactly when you need it—without the burden of full-time employment costs.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-lg text-slate-600 mb-6">
            Ready to lean out your overhead?
          </p>
          <a
            href="#contact"
            className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap"
          >
            Get a Quote
          </a>
        </div>
      </div>
    </section>
  );
}
